import { lazy, Suspense, useEffect, useState } from 'react';
import { Link, Navigate, NavLink, Outlet, Route, Routes } from 'react-router-dom';
import {
  Avatar,
  AvatarFallback,
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  SafeMindsLogo,
  useTheme,
  TooltipProvider,
  cn,
} from '@safe-minds-ai/ui';
import { useAppDispatch, useAppSelector, setChannelStatus, setLastEvent, setSession, setStatus, signOut } from '@safe-minds-ai/state';
import { useQuery } from '@tanstack/react-query';
import { fetchSession, fetchParentProfile } from './api';
import { clearAccessToken, clearRefreshToken, getAccessToken } from '@safe-minds-ai/utils';
import { openEventStream, RealtimeChannel } from '@safe-minds-ai/api';
import {
  HelpCircle,
  LogOut,
  Users,
  Clock,
  Settings,
  ChevronRight,
  Shield,
} from 'lucide-react';
import { logout } from '@safe-minds-ai/api';
import { ToastProvider } from './toast';

const DashboardRoute = lazy(() => import('./routes/dashboard'));
const HouseholdRoute = lazy(() => import('./routes/household'));
const SettingsRoute = lazy(() => import('./routes/settings'));
const ManageAccountRoute = lazy(() => import('./routes/manage-account'));
const AccountPlanRoute = lazy(() => import('./routes/account-plan'));
const AccountPrivacyRoute = lazy(() => import('./routes/account-privacy'));
const AccountBillingRoute = lazy(() => import('./routes/account-billing'));
const AccountActivityRoute = lazy(() => import('./routes/account-activity'));
const LandingRoute = lazy(() => import('./routes/landing'));
const AuthLoginRoute = lazy(() => import('./routes/auth-login'));
const AuthSignupRoute = lazy(() => import('./routes/auth-signup'));
const AccessStatusRoute = lazy(() => import('./routes/access-status'));
const VerifyEmailRoute = lazy(() => import('./routes/verify-email'));
const ConsentRoute = lazy(() => import('./routes/consent'));
const PasswordResetRoute = lazy(() => import('./routes/password-reset'));
const OnboardingRoute = lazy(() => import('./routes/onboarding'));
const ChildrenRoute = lazy(() => import('./routes/children'));
const ParentalControlsRoute = lazy(() => import('./routes/parental-controls'));
const AlertsRoute = lazy(() => import('./routes/alerts'));
const ChatRoute = lazy(() => import('./routes/chat'));
const SupportRoute = lazy(() => import('./routes/support'));

function toTitleCaseSegment(value: string) {
  if (!value) return '';
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
}

function getCompactParentName(input?: string | null) {
  const value = input?.trim();
  if (!value) return 'Parent';
  if (value.includes('@')) {
    const localPart = value.split('@')[0] ?? '';
    const firstSegment = localPart.split(/[._-]+/).find(Boolean) ?? localPart;
    return toTitleCaseSegment(firstSegment) || 'Parent';
  }
  const firstSegment = value.split(/\s+/).find(Boolean) ?? value;
  return toTitleCaseSegment(firstSegment) || 'Parent';
}

function ParentLayout(): JSX.Element {
  const dispatch = useAppDispatch();
  const authStatus = useAppSelector((state) => state.auth.status);
  const session = useAppSelector((state) => state.auth.session);

  const { data: sessionData, error: sessionError, isFetching: sessionLoading } = useQuery({
    queryKey: ['auth', 'session'],
    queryFn: fetchSession,
    retry: false,
    enabled: authStatus === 'unknown' || authStatus === 'loading',
  });

  const { data: profile } = useQuery({
    queryKey: ['parent', 'profile', 'nav'],
    queryFn: fetchParentProfile,
    enabled: authStatus === 'authenticated',
  });

  const { resolvedTheme } = useTheme();
  const logoVariant = resolvedTheme === 'dark' ? 'full-dark' : 'full-light';

  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (authStatus === 'unknown' && sessionLoading) {
      dispatch(setStatus('loading'));
    }
  }, [authStatus, sessionLoading, dispatch]);

  useEffect(() => {
    if (sessionData) {
      dispatch(setSession(sessionData));
    }
  }, [dispatch, sessionData]);

  useEffect(() => {
    if (sessionError) {
      clearAccessToken();
      clearRefreshToken();
      dispatch(setStatus('unauthenticated'));
    }
  }, [dispatch, sessionError]);

  useEffect(() => {
    if (authStatus !== 'authenticated') return;
    const token = getAccessToken();
    const channels: RealtimeChannel[] = ['flags', 'usage', 'chat'];
    const connections = channels.map((channel) => {
      const connection = openEventStream({
        channel,
        token,
        onOpen: () => dispatch(setChannelStatus({ channel, connected: true })),
        onMessage: (event) => dispatch(setLastEvent(event)),
        onError: () => dispatch(setChannelStatus({ channel, connected: false })),
      });
      return { channel, connection };
    });
    return () => {
      connections.forEach(({ channel, connection }) => {
        dispatch(setChannelStatus({ channel, connected: false }));
        connection.close();
      });
    };
  }, [authStatus, dispatch]);

  if (authStatus === 'loading' || authStatus === 'unknown') {
    return (
      <div className="flex min-h-screen items-center justify-center text-muted-foreground">
        Checking your session…
      </div>
    );
  }

  if (authStatus === 'unauthenticated') {
    return <Navigate to="/login" replace />;
  }

  const subscriptionType = profile?.subscriptionType ?? 'FREE';
  const planName = subscriptionType === 'PAID' ? 'Premium plan' : 'Free plan';
  const planStatus = subscriptionType === 'PAID' ? 'Active' : 'Free tier';
  const renewalText = subscriptionType === 'PAID' ? 'Renews monthly' : 'Not billed';
  const paymentText = subscriptionType === 'PAID' ? 'Payment on file' : 'No payment required';
  const profileDisplayName = [profile?.firstName, profile?.lastName].filter(Boolean).join(' ').trim();
  const compactParentName = getCompactParentName(profile?.firstName || session?.displayName || session?.email);
  const fullParentName = profileDisplayName || session?.displayName || compactParentName;
  const parentEmail = profile?.user?.email || session?.email || 'parent@safeminds.ai';
  const parentInitials = compactParentName.slice(0, 2).toUpperCase() || 'SM';

  const navItems = [
    { to: '/dashboard', label: 'Dashboard', end: true },
    { to: '/children', label: 'Children' },
    { to: '/parental-controls', label: 'Parental Controls' },
    { to: '/alerts', label: 'Alerts' },
    { to: '/chat', label: 'Chat History' },
    { to: '/settings', label: 'Settings' },
  ];

  return (
    <div className="sm-page pb-8 pt-4 md:py-8">
      <div className="sm-container flex flex-col gap-8">
        <header className="sm-surface-card overflow-visible p-4 md:p-6">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between xl:gap-6">
            <div className="flex min-w-0 flex-1 flex-col gap-4 lg:flex-row lg:items-center lg:gap-8">
              <SafeMindsLogo variant={logoVariant} size={230} className="shrink-0" />
              <div className="-mx-2 min-w-0 overflow-x-auto px-2 py-2">
                <nav className="flex flex-nowrap gap-3 whitespace-nowrap text-sm md:gap-4">
                  {navItems.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.end}
                      className={({ isActive }) =>
                        cn(
                          'rounded-full px-4 py-2 text-sm font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--sm-focus)]',
                          isActive
                            ? 'bg-[color:var(--sm-primary)] text-white'
                            : 'text-[color:var(--sm-text-muted)] hover:text-[color:var(--sm-text)] hover:bg-[color:var(--sm-surface-muted)]'
                        )
                      }
                    >
                      {item.label}
                    </NavLink>
                  ))}
                </nav>
              </div>
            </div>
            <div className="flex w-full items-center justify-between gap-3 sm:w-auto sm:justify-end xl:shrink-0">
              <Button asChild variant="outline" size="icon" className="rounded-full border-[color:var(--sm-border)] bg-[color:var(--sm-surface)] text-[color:var(--sm-text)] hover:bg-[color:var(--sm-surface-muted)]">
                <Link to="/support">
                  <HelpCircle className="h-4 w-4" />
                </Link>
              </Button>
              <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
                <DropdownMenuTrigger asChild>
                  <button className="flex max-w-[220px] items-center gap-3 rounded-full border border-[color:var(--sm-border)] px-3 py-2 text-sm font-semibold text-[color:var(--sm-text)] transition hover:border-primary/60 hover:bg-[color:var(--sm-surface-muted)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--sm-focus)] sm:max-w-[260px]">
                    <Avatar className="h-10 w-10 border border-[color:var(--sm-border)]">
                      <AvatarFallback>{parentInitials}</AvatarFallback>
                    </Avatar>
                    <span className="min-w-0 truncate text-left">{compactParentName}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80 rounded-[28px] border border-[color:var(--sm-border)] bg-[color:var(--sm-surface)] text-[color:var(--sm-text)] shadow-2xl">
                  <DropdownMenuLabel className="flex items-center gap-3 p-4">
                    <Avatar className="h-12 w-12 border-2 border-primary/30">
                      <AvatarFallback>{parentInitials}</AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                      <p className="truncate text-base font-semibold">{fullParentName}</p>
                      <p className="truncate text-xs text-[color:var(--sm-text-muted)]">{parentEmail}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <div className="space-y-1 px-3">
                    {[
                      { label: 'Manage account', href: '/account', icon: Settings },
                      { label: 'Child profiles', href: '/children', icon: Users },
                      { label: 'Controls', href: '/parental-controls', icon: Shield },
                      { label: 'Activity & usage', href: '/alerts', icon: Clock },
                      { label: 'Support', href: '/support', icon: HelpCircle },
                    ].map((item) => (
                      <DropdownMenuItem
                        key={item.label}
                        asChild
                        className="group flex items-center justify-between rounded-2xl px-4 py-3 text-sm font-medium text-[color:var(--sm-text)] transition hover:bg-[color:var(--sm-surface-muted)]"
                      >
                        <Link className="flex w-full items-center justify-between gap-3" to={item.href} onClick={() => setMenuOpen(false)}>
                          <span className="flex items-center gap-3">
                            <item.icon className="h-4 w-4 text-primary" />
                            {item.label}
                          </span>
                          <ChevronRight className="h-4 w-4 text-[color:var(--sm-text-muted)] transition group-hover:text-[color:var(--sm-text)]" />
                        </Link>
                      </DropdownMenuItem>
                    ))}
                  </div>
                  <DropdownMenuSeparator />
                  <div className="space-y-3 px-4 pb-4">
                    <div className="rounded-[28px] border border-[color:var(--sm-border)] bg-gradient-to-br from-primary/5 via-white to-primary/5 p-5 shadow-sm">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-semibold text-slate-900">{planName}</p>
                          <p className="text-xs text-[color:var(--sm-text-muted)]">{renewalText} · {paymentText}</p>
                        </div>
                        <Badge variant="secondary" className="rounded-full px-3 py-1 text-[11px] uppercase tracking-[0.2em] whitespace-nowrap">
                          {planStatus}
                        </Badge>
                      </div>
                      <div className="mt-4 flex flex-wrap gap-2">
                        <Button asChild size="sm" className="flex-1 min-w-[140px] rounded-full sm-btn-primary">
                          <Link to="/account" onClick={() => setMenuOpen(false)}>Manage plan</Link>
                        </Button>
                        <Button asChild size="sm" variant="outline" className="flex-1 min-w-[140px] rounded-full sm-btn-secondary">
                          <Link to="/settings" onClick={() => setMenuOpen(false)}>View invoices</Link>
                        </Button>
                      </div>
                    </div>
                    <div className="rounded-2xl border border-dashed border-[color:var(--sm-border)] px-4 py-3 text-xs text-[color:var(--sm-text-muted)]">
                      <p className="font-medium text-[color:var(--sm-text)]">Need adjustments?</p>
                      <p>Upgrade, pause, or switch plans with our support team.</p>
                    </div>
                    <DropdownMenuItem
                      className="flex items-center gap-3 rounded-2xl px-4 py-2 text-sm text-orange-500 hover:bg-orange-50"
                      onSelect={(event) => {
                        event.preventDefault();
                        void (async () => {
                          await logout();
                          dispatch(signOut());
                        })();
                      }}
                    >
                      <LogOut className="mr-3 inline h-4 w-4" />
                      Log out
                    </DropdownMenuItem>
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>
        <main className="space-y-8">
          <Suspense fallback={<div className="sm-surface-card p-8">Loading...</div>}>
            <Outlet />
          </Suspense>
        </main>
        <footer className="pb-4 text-center text-sm text-[color:var(--sm-text-muted)]">
          © {new Date().getFullYear()} SafeMindsAI. Built with care for growing minds.
        </footer>
      </div>
    </div>
  );
}

export default function App(): JSX.Element {
  return (
    <ToastProvider>
      <TooltipProvider>
        <Suspense fallback={<div className="flex min-h-screen items-center justify-center text-muted-foreground">Loading...</div>}>
          <Routes>
            <Route path="/" element={<Navigate to="/landing" replace />} />
            <Route path="/landing" element={<LandingRoute />} />
            <Route path="/login" element={<AuthLoginRoute />} />
            <Route path="/signup" element={<AuthSignupRoute />} />
            <Route path="/access-status" element={<AccessStatusRoute />} />
            <Route path="/verify-email" element={<VerifyEmailRoute />} />
            <Route path="/consent" element={<ConsentRoute />} />
            <Route path="/forgot-password" element={<PasswordResetRoute />} />
            <Route path="/reset-password" element={<PasswordResetRoute />} />
            <Route path="/welcome" element={<Navigate to="/dashboard" replace />} />
            <Route element={<ParentLayout />}>
              <Route path="/dashboard" element={<DashboardRoute />} />
              <Route path="/children" element={<ChildrenRoute />} />
              <Route path="/children/:childId" element={<HouseholdRoute />} />
              <Route path="/parental-controls" element={<ParentalControlsRoute />} />
              <Route path="/alerts" element={<AlertsRoute />} />
              <Route path="/chat" element={<ChatRoute />} />
              <Route path="/settings" element={<SettingsRoute />} />
              <Route path="/account" element={<ManageAccountRoute />} />
              <Route path="/account/plan" element={<AccountPlanRoute />} />
              <Route path="/account/privacy" element={<AccountPrivacyRoute />} />
              <Route path="/account/billing" element={<AccountBillingRoute />} />
              <Route path="/account/activity" element={<AccountActivityRoute />} />
              <Route path="/support" element={<SupportRoute />} />
              <Route path="/onboarding" element={<OnboardingRoute />} />
            </Route>
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Suspense>
      </TooltipProvider>
    </ToastProvider>
  );
}
